import React from "react";
import ReactDOM from "react-dom";
import { ExclamationCircleFilled, CloseOutlined } from "@ant-design/icons";
import ButtonItem from "./ButtonItem";

// Inyectar animaciones una sola vez (mismo patrón que ButtonItem.js)
if (typeof document !== "undefined") {
  const styleId = "biwo-error-overlay-styles";
  if (!document.getElementById(styleId)) {
    const styleElement = document.createElement("style");
    styleElement.id = styleId;
    styleElement.textContent = `
      @keyframes biwoErrorFadeIn {
        from { opacity: 0; }
        to   { opacity: 1; }
      }
      @keyframes biwoErrorPopIn {
        from { opacity: 0; transform: translateY(12px) scale(0.96); }
        to   { opacity: 1; transform: translateY(0) scale(1); }
      }
    `;
    document.head.appendChild(styleElement);
  }
}

const CONTAINER_ID = "biwo-error-overlay-container";

// Convierte el nombre de la lista a texto legible: "insumosAUtilizar" → "insumos a utilizar"
const nombreLegible = (nombre) =>
  String(nombre)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase();

// Singular aproximado para etiquetar cada fila:
// "insumos" → "insumo", "retenciones" → "retención", "colaboradores" → "colaborador"
const singular = (nombre) => {
  const palabra = nombreLegible(nombre).split(" ")[0];
  if (palabra.endsWith("ciones")) return palabra.slice(0, -6) + "ción";
  if (palabra.endsWith("siones")) return palabra.slice(0, -6) + "sión";
  if (palabra.endsWith("ores")) return palabra.slice(0, -2);
  if (/[aeiou]s$/.test(palabra)) return palabra.slice(0, -1);
  return palabra;
};

const capitalizar = (texto) => texto.charAt(0).toUpperCase() + texto.slice(1);

// Desmonta y elimina el overlay del DOM
function cerrarOverlay() {
  const el = document.getElementById(CONTAINER_ID);
  if (el) {
    ReactDOM.unmountComponentAtNode(el);
    el.remove();
  }
}

// Monta un overlay nuevo
function montarOverlay(render) {
  cerrarOverlay();
  const container = document.createElement("div");
  container.id = CONTAINER_ID;
  document.body.appendChild(container);
  ReactDOM.render(render(cerrarOverlay), container);
}

function ErrorCard({ children }) {
  return (
    <li
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        padding: "8px 12px",
        marginBottom: 6,
        background: "#fff2f0",
        border: "1px solid #ffccc7",
        borderRadius: 8,
        color: "#a8071a",
        fontSize: 14,
        lineHeight: 1.4,
      }}
    >
      <span style={{ color: "#ff4d4f", fontWeight: 700, flexShrink: 0 }}>•</span>
      <span>{children}</span>
    </li>
  );
}

// El contenido central se pasa como children.
function OverlayShell({ titulo, onClose, children }) {
  // Cerrar con la tecla Escape
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="biwo-error-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        background: "rgba(0, 0, 0, 0.55)",
        animation: "biwoErrorFadeIn 0.2s ease-out",
        fontFamily: "'Plus Jakarta Sans', sans-serif",
      }}
    >
      <div
        className="biwo-error-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 460,
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          background: "#ffffff",
          borderRadius: 16,
          // Marco rojo + margen rojo suave (ring) + sombra
          border: "2px solid #ff4d4f",
          boxShadow:
            "0 16px 48px rgba(0, 0, 0, 0.28), 0 0 0 6px rgba(255, 77, 79, 0.14)",
          padding: 24,
          animation: "biwoErrorPopIn 0.22s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {/* Botón X en la esquina */}
        <span
          onClick={onClose}
          style={{
            position: "absolute",
            top: 14,
            right: 16,
            cursor: "pointer",
            color: "#ff4d4f",
            fontSize: 15,
            lineHeight: 1,
          }}
        >
          <CloseOutlined />
        </span>

        {/* Encabezado */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 12,
            paddingRight: 24,
          }}
        >
          <ExclamationCircleFilled style={{ color: "#ff4d4f", fontSize: 26 }} />
          <span style={{ fontWeight: 700, fontSize: 17, color: "#a8071a" }}>
            {titulo}
          </span>
        </div>

        {children}

        {/* Botón de cerrar (ButtonItem) */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: 20,
            paddingTop: 16,
            borderTop: "1px solid #f0f0f0",
          }}
        >
          <ButtonItem rojo lessTime onClick={onClose} style={{ width: 150 }}>
            Entendido
          </ButtonItem>
        </div>
      </div>
    </div>
  );
}

// Cuerpo para errores de validación de formularios (campos sueltos + tablas)
function CuerpoErroresForm({ sueltos, grupos, total }) {
  return (
    <>
      <p style={{ margin: "0 0 12px", color: "rgba(0,0,0,0.65)" }}>
        Revisa {total === 1 ? "este campo" : "estos campos"}:
      </p>

      {/* Lista de errores (con scroll si son muchos) */}
      <div style={{ overflowY: "auto", flex: "1 1 auto" }}>
        {/* Campos sueltos del formulario */}
        {sueltos.length > 0 && (
          <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none" }}>
            {sueltos.map((msg, index) => (
              <ErrorCard key={index}>{msg}</ErrorCard>
            ))}
          </ul>
        )}

        {/* Errores agrupados por tabla */}
        {grupos.map((grupo) => (
          <div key={grupo.lista}>
            <p
              style={{
                margin: "10px 0 6px",
                fontSize: 13,
                fontWeight: 600,
                color: "rgba(0,0,0,0.65)",
              }}
            >
              En la tabla de {grupo.titulo}:
            </p>
            <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none" }}>
              {grupo.filas.map((f, index) => (
                <ErrorCard key={index}>
                  <b>
                    {grupo.etiqueta} #{f.fila}:
                  </b>{" "}
                  {f.texto}
                </ErrorCard>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </>
  );
}

// Overlay de error para validaciones de formularios (antd onFinishFailed)
export function ErrorItem(e) {
  const errorFields = (e && e.errorFields) || [];

  // Separa errores de campos sueltos y errores de filas de tablas (Form.List)
  const sueltos = [];
  const grupos = []; // [{ lista, titulo, etiqueta, filas: [{ fila, texto }] }]

  errorFields.forEach((field) => {
    const texto =
      field.errors && field.errors.length
        ? field.errors.join(", ")
        : "Error desconocido";

    const name = Array.isArray(field.name) ? field.name : [field.name];
    const rowIndex = name[1];

    if (rowIndex !== undefined && typeof name[0] !== "undefined") {
      const lista = String(name[0]);
      let grupo = grupos.find((g) => g.lista === lista);
      if (!grupo) {
        grupo = {
          lista,
          titulo: nombreLegible(lista),
          etiqueta: capitalizar(singular(lista)),
          filas: [],
        };
        grupos.push(grupo);
      }
      grupo.filas.push({ fila: rowIndex + 1, texto });
    } else {
      sueltos.push(texto);
    }
  });

  const total = sueltos.length + grupos.reduce((n, g) => n + g.filas.length, 0);

  if (total === 0) {
    return;
  }

  montarOverlay((onClose) => (
    <OverlayShell titulo="No se pudo guardar" onClose={onClose}>
      <CuerpoErroresForm sueltos={sueltos} grupos={grupos} total={total} />
    </OverlayShell>
  ));
}

// Detalle técnico plegado: el texto crudo del SAT/PAC, que no le sirve a quien captura
// pero es lo único que sirve para reportar el caso.
//
// Va en un <details> y no en estado de React a propósito: este árbol se monta con
// ReactDOM.render en un div desprendido (ver montarOverlay), así que entre menos
// estado tenga, menos hay que sostener. El <details> nativo ya viene cerrado y se
// abre con el teclado.
function DetalleTecnico({ texto }) {
  const [copiado, setCopiado] = React.useState(false);

  const copiar = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // El portapapeles no existe en http sin localhost; si falla, el texto igual se ve.
    try {
      navigator.clipboard.writeText(texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch (err) {
      setCopiado(false);
    }
  };

  return (
    <details style={{ marginTop: 12 }}>
      <summary
        style={{
          cursor: "pointer",
          fontSize: 13,
          color: "rgba(0,0,0,0.55)",
          userSelect: "none",
        }}
      >
        Ver detalle técnico del SAT
      </summary>
      <pre
        style={{
          margin: "8px 0 0",
          padding: "8px 10px",
          maxHeight: 180,
          overflow: "auto",
          background: "#fafafa",
          border: "1px solid #f0f0f0",
          borderRadius: 6,
          color: "rgba(0,0,0,0.65)",
          fontSize: 12,
          lineHeight: 1.5,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          fontFamily: "'SFMono-Regular', Consolas, monospace",
        }}
      >
        {texto}
      </pre>
      <span
        onClick={copiar}
        style={{
          display: "inline-block",
          marginTop: 6,
          cursor: "pointer",
          fontSize: 12,
          color: copiado ? "#389e0d" : "#1890ff",
        }}
      >
        {copiado ? "Copiado" : "Copiar para soporte"}
      </span>
    </details>
  );
}

// Overlay de error para un mensaje del backend.
//
// Uso mínimo, el de siempre:
//   MensajeError({ message: res.data.message, description: res.data.description })
//
// Para un rechazo de timbrado, el backend manda además `res.data.errorTimbrado`:
//   MensajeError({
//     message:        res.data.errorTimbrado?.titulo  || res.data.message,
//     description:    res.data.errorTimbrado?.mensaje || res.data.description,
//     detalleTecnico: res.data.errorTimbrado?.detalleTecnico,
//   })
//
// Sin `detalleTecnico` se ve exactamente como antes, así que los llamados que ya
// existen no cambian.
//
// Ojo: `errorTimbrado.tipo` (datos / sistema / desconocido) NO se recibe ni se pinta.
// Esa clasificación es interna —sólo decide si el error entra al log del servidor— y
// al usuario no se le dice nunca "es un problema del sistema": aunque la regla del SAT
// hable de un total o de un redondeo, la causa de fondo casi siempre es un dato
// capturado, y decirle que no es suyo lo deja sin nada que revisar.
export function MensajeError({ message, description, detalleTecnico, onClose } = {}) {
  const titulo = message || "Ocurrió un error";

  montarOverlay((cerrar) => (
    <OverlayShell
      titulo={titulo}
      onClose={() => {
        cerrar();
        onClose?.();
      }}
    >
      {description || detalleTecnico ? (
        <div style={{ overflowY: "auto", flex: "1 1 auto" }}>
          {description ? (
            <div
              style={{
                padding: "10px 14px",
                background: "#fff2f0",
                border: "1px solid #ffccc7",
                borderRadius: 8,
                color: "#a8071a",
                fontSize: 14,
                lineHeight: 1.45,
                // Respeta los saltos de línea (\n) que vengan en la descripción
                whiteSpace: "pre-line",
              }}
            >
              {description}
            </div>
          ) : null}

          {detalleTecnico ? <DetalleTecnico texto={detalleTecnico} /> : null}
        </div>
      ) : null}
    </OverlayShell>
  ));
}

export default ErrorItem;
